import React, { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { Loader2, ShieldCheck, AlertCircle, Check, X, ScrollText, BarChart3, TrendingUp, Lock, Users as UsersIcon, Download, ZoomIn } from "lucide-react";
import { toast } from "sonner";

/**
 * Admin P2P management panel.
 * - Overview stats (live trades, completed, KYC stats, escrow lock)
 * - KYC queue (India submissions awaiting review)
 * - Active disputes
 * - All trades audit log
 */
const P2PAdminPanel = () => {
  const { authAxios } = useAuth();
  const api = authAxios();

  const [subTab, setSubTab] = useState("overview"); // overview | kyc | disputes | trades | ads
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [kycList, setKycList] = useState([]);
  const [disputes, setDisputes] = useState([]);
  const [trades, setTrades] = useState([]);
  const [adminAds, setAdminAds] = useState([]);
  const [adsFilterCountry, setAdsFilterCountry] = useState(""); // ""|india|pakistan|nigeria
  const [adsFilterStatus, setAdsFilterStatus] = useState("active");
  // Per-submission text inputs for ID number + address before approve
  const [kycInputs, setKycInputs] = useState({}); // {submissionId: {id_number, address}}
  // Lightbox state — Chrome blocks navigation to data: URLs in new tabs, so we render inline.
  const [lightbox, setLightbox] = useState(null); // { url, title }

  const fetchStats = async () => {
    setLoading(true);
    try {
      const r = await api.get("/p2p/admin/stats");
      setStats(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
    setLoading(false);
  };

  const fetchKyc = async () => {
    setLoading(true);
    try {
      const r = await api.get("/p2p/admin/kyc/queue");
      setKycList(r.data.submissions || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
    setLoading(false);
  };

  const fetchDisputes = async () => {
    setLoading(true);
    try {
      const r = await api.get("/p2p/admin/disputes?status=open");
      setDisputes(r.data.disputes || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
    setLoading(false);
  };

  const fetchTrades = async () => {
    setLoading(true);
    try {
      const r = await api.get("/p2p/admin/trades?limit=100");
      setTrades(r.data.trades || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
    setLoading(false);
  };

  const fetchAdminAds = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (adsFilterCountry) params.append("country", adsFilterCountry);
      if (adsFilterStatus) params.append("status", adsFilterStatus);
      params.append("limit", "200");
      const r = await api.get(`/p2p/admin/ads?${params.toString()}`);
      setAdminAds(r.data.ads || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
    setLoading(false);
  };

  useEffect(() => {
    if (subTab === "overview") fetchStats();
    else if (subTab === "kyc") fetchKyc();
    else if (subTab === "disputes") fetchDisputes();
    else if (subTab === "ads") fetchAdminAds();
    else fetchTrades();
    // eslint-disable-next-line
  }, [subTab]);

  // refetch ads when filters change
  useEffect(() => {
    if (subTab === "ads") fetchAdminAds();
    // eslint-disable-next-line
  }, [adsFilterCountry, adsFilterStatus]);

  const updateKycInput = (id, field, value) => {
    setKycInputs((prev) => ({ ...prev, [id]: { ...(prev[id] || {}), [field]: value } }));
  };

  const approveKyc = async (id) => {
    const inputs = kycInputs[id] || {};
    const sub = kycList.find((k) => k.id === id);
    const isNg = sub?.country === "nigeria";
    const idNumber = (inputs.id_number || "").trim();
    const address = (inputs.address || "").trim();
    const ninNumber = (inputs.nin_number || "").trim();
    if (!idNumber || idNumber.length < 4) {
      toast.error(isNg ? "Enter BVN (11 digits, from slip)" : "Enter Aadhar ID number (read from image)");
      return;
    }
    if (!address || address.length < 5) {
      toast.error("Enter address (read from image)");
      return;
    }
    if (isNg && !/^\d{11}$/.test(ninNumber)) {
      toast.error("Enter NIN (11 digits, from slip)");
      return;
    }
    try {
      await api.post(`/p2p/admin/kyc/${id}/approve`, {
        id_number: idNumber,
        address,
        nin_number: isNg ? ninNumber : undefined,
        notes: "",
      });
      toast.success("KYC approved. Images purged from DB.");
      fetchKyc();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const rejectKyc = async (id) => {
    const notes = window.prompt("Rejection reason:");
    if (!notes) return;
    try {
      await api.post(`/p2p/admin/kyc/${id}/reject`, { notes });
      toast.success("KYC rejected");
      fetchKyc();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const resolveDispute = async (id, resolution) => {
    const notes = window.prompt(`Notes for ${resolution.toUpperCase()}:`);
    if (notes === null) return;
    try {
      const r = await api.post(`/p2p/admin/disputes/${id}/resolve`, { resolution, notes });
      toast.success(`Resolved: ${r.data.trade_status}`);
      fetchDisputes();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="space-y-4">
      {/* Sub tabs */}
      <div className="flex gap-2 flex-wrap">
        {[
          { k: "overview", l: "Overview", icon: BarChart3 },
          { k: "kyc", l: "KYC Queue", icon: ShieldCheck },
          { k: "ads", l: "Live Ads", icon: TrendingUp },
          { k: "disputes", l: "Disputes", icon: AlertCircle },
          { k: "trades", l: "All Trades", icon: ScrollText },
        ].map((t) => (
          <button
            key={t.k}
            onClick={() => setSubTab(t.k)}
            className={`flex-1 min-w-[110px] py-2 rounded-lg text-xs flex items-center justify-center gap-1 ${subTab === t.k ? "bg-primary text-black font-semibold" : "bg-card"}`}
            data-testid={`p2p-admin-tab-${t.k}`}
          >
            <t.icon size={14} /> {t.l}
          </button>
        ))}
      </div>

      {loading && <div className="flex justify-center py-6"><Loader2 className="animate-spin" /></div>}

      {/* Overview / Stats */}
      {!loading && subTab === "overview" && stats && (
        <div className="space-y-3" data-testid="p2p-overview-stats">
          {/* KYC status banner */}
          <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-3 flex items-center gap-2">
            <Check size={18} className="text-green-400" />
            <div className="text-xs">
              <div className="font-semibold text-green-300">KYC Function: Operational ✓</div>
              <div className="text-muted-foreground">
                {stats.kyc.pending_review} pending review • {stats.kyc.approved} approved • {stats.kyc.users_verified} users verified
              </div>
            </div>
          </div>

          {/* Headline numbers */}
          <div className="grid grid-cols-2 gap-2">
            <StatCard label="🔴 Live Trades" value={stats.trades.live} hint="awaiting payment / KYC / disputed" testId="stat-live-trades" />
            <StatCard label="✅ Completed Trades" value={stats.trades.completed} hint="successful releases" testId="stat-completed" />
            <StatCard label="🚫 Cancelled" value={stats.trades.cancelled} hint="lifetime" testId="stat-cancelled" />
            <StatCard label="⚖️ Open Disputes" value={stats.disputes.open} hint={`${stats.disputes.resolved} resolved`} testId="stat-disputes" />
          </div>

          {/* People */}
          <div className="bg-card border border-border rounded-xl p-4 space-y-2" data-testid="stat-users">
            <div className="text-sm font-semibold flex items-center gap-2">
              <UsersIcon size={16} className="text-primary" /> People Trading (lifetime)
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <div className="text-2xl font-bold text-primary">{stats.trades.unique_buyers}</div>
                <div className="text-xs text-muted-foreground">Unique Buyers</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-primary">{stats.trades.unique_sellers}</div>
                <div className="text-xs text-muted-foreground">Unique Sellers</div>
              </div>
            </div>
          </div>

          {/* Volume by status */}
          <div className="bg-card border border-border rounded-xl p-4 space-y-2">
            <div className="text-sm font-semibold flex items-center gap-2">
              <TrendingUp size={16} className="text-primary" /> Volume by Status
            </div>
            {Object.entries(stats.trades.by_status).length === 0 ? (
              <div className="text-xs text-muted-foreground">No trades yet</div>
            ) : (
              Object.entries(stats.trades.by_status).map(([s, v]) => (
                <div key={s} className="flex items-center justify-between text-xs border-b border-border/40 py-1.5 last:border-0">
                  <span className="text-muted-foreground uppercase">{s.replace("_", " ")}</span>
                  <span><b>{v.count}</b> trades • <span className="text-primary">{v.volume_usdt}</span> USDT</span>
                </div>
              ))
            )}
          </div>

          {/* Country breakdown */}
          <div className="bg-card border border-border rounded-xl p-4 space-y-2">
            <div className="text-sm font-semibold">Completed Trades by Country</div>
            {Object.entries(stats.trades.by_country).length === 0 ? (
              <div className="text-xs text-muted-foreground">No completed trades yet</div>
            ) : (
              Object.entries(stats.trades.by_country).map(([c, v]) => (
                <div key={c} className="flex items-center justify-between text-xs">
                  <span className="uppercase">{c === "india" ? "🇮🇳 India" : c === "pakistan" ? "🇵🇰 Pakistan" : c === "nigeria" ? "🇳🇬 Nigeria" : c}</span>
                  <span><b>{v.count}</b> trades • <span className="text-primary">{v.volume_usdt}</span> USDT</span>
                </div>
              ))
            )}
          </div>

          {/* Ads / escrow */}
          <div className="bg-card border border-border rounded-xl p-4 space-y-2">
            <div className="text-sm font-semibold flex items-center gap-2">
              <Lock size={16} className="text-primary" /> Ads & Escrow
            </div>
            <div className="grid grid-cols-3 gap-2 text-center text-xs">
              <div><div className="text-lg font-bold">{stats.ads.active}</div><div className="text-muted-foreground">Active Ads</div></div>
              <div><div className="text-lg font-bold">{stats.ads.total}</div><div className="text-muted-foreground">Total Ads</div></div>
              <div><div className="text-lg font-bold text-primary">{stats.ads.locked_usdt_in_escrow}</div><div className="text-muted-foreground">USDT Locked</div></div>
            </div>
          </div>

          <button
            onClick={fetchStats}
            className="w-full py-2 border border-border rounded-lg text-xs"
            data-testid="refresh-stats-btn"
          >
            🔄 Refresh Stats
          </button>
        </div>
      )}

      {/* KYC Queue */}
      {!loading && subTab === "kyc" && (
        <div className="space-y-3">
          {kycList.length === 0 && <div className="text-center py-8 text-muted-foreground text-sm">No pending KYC submissions</div>}
          {kycList.map((k) => {
            const kycCountry = k.country || "india";
            const isNg = kycCountry === "nigeria";
            return (
            <div key={k.id} className="bg-card border border-border rounded-xl p-3 space-y-2" data-testid={`kyc-row-${k.id}`}>
              <div className="flex items-center justify-between">
                <div className="text-sm font-semibold">{k.user?.full_name} ({k.user?.phone})</div>
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-primary/20 text-primary uppercase">
                  {isNg ? "🇳🇬 NG" : kycCountry === "india" ? "🇮🇳 IN" : kycCountry}
                </span>
              </div>
              <div className="text-xs text-muted-foreground">Trade: #{(k.trade_id || "standalone").slice(0, 8)} • Submitted: {new Date(k.created_at).toLocaleString()}</div>
              <div className="grid grid-cols-3 gap-2">
                <Thumb url={k.aadhar_front_b64} label={isNg ? "BVN slip" : "Aadhar front"} onOpen={setLightbox} testId={`kyc-img-front-${k.id}`} />
                <Thumb url={k.aadhar_back_b64} label={isNg ? "NIN slip" : "Aadhar back"} onOpen={setLightbox} testId={`kyc-img-back-${k.id}`} />
                <Thumb url={k.selfie_b64} label="Selfie" onOpen={setLightbox} testId={`kyc-img-selfie-${k.id}`} />
              </div>
              <div className="text-xs">Submitted phone: <span className="font-mono">{k.phone}</span></div>
              {isNg && (
                <div className="text-xs space-y-0.5">
                  <div>User-typed BVN: <span className="font-mono text-primary">{k.bvn_number || "—"}</span></div>
                  <div>User-typed NIN: <span className="font-mono text-primary">{k.nin_number || "—"}</span></div>
                </div>
              )}

              {/* Required text inputs — admin reads from ID image */}
              <div className="space-y-2 pt-1 bg-background/50 -mx-1 px-2 py-2 rounded-lg border border-yellow-500/20">
                <div className="text-[10px] uppercase tracking-wider text-yellow-400 font-semibold">
                  {isNg
                    ? "Read from BVN slip & type below (required before approve):"
                    : "Read from Aadhar & type below (required before approve):"}
                </div>
                <input
                  value={(kycInputs[k.id] || {}).id_number || ""}
                  onChange={(e) => updateKycInput(k.id, "id_number", e.target.value)}
                  placeholder={isNg ? "BVN Number (11 digits, from slip)" : "Aadhar Number (12 digits)"}
                  className="w-full bg-background border border-border rounded-lg px-2 py-1.5 text-xs"
                  data-testid={`kyc-idnum-${k.id}`}
                />
                {isNg && (
                  <input
                    value={(kycInputs[k.id] || {}).nin_number || ""}
                    onChange={(e) => updateKycInput(k.id, "nin_number", e.target.value)}
                    placeholder="NIN Number (11 digits, from slip)"
                    className="w-full bg-background border border-border rounded-lg px-2 py-1.5 text-xs"
                    data-testid={`kyc-nin-${k.id}`}
                  />
                )}
                <textarea
                  value={(kycInputs[k.id] || {}).address || ""}
                  onChange={(e) => updateKycInput(k.id, "address", e.target.value)}
                  placeholder={isNg ? "Full Address (from BVN slip)" : "Full Address (from Aadhar)"}
                  rows={2}
                  className="w-full bg-background border border-border rounded-lg px-2 py-1.5 text-xs"
                  data-testid={`kyc-address-${k.id}`}
                />
              </div>

              <div className="flex gap-2 pt-1">
                <button onClick={() => approveKyc(k.id)} className="flex-1 py-2 bg-green-600 text-white rounded-lg text-xs font-semibold flex items-center justify-center gap-1" data-testid={`approve-kyc-${k.id}`}>
                  <Check size={14} /> Approve (images will delete)
                </button>
                <button onClick={() => rejectKyc(k.id)} className="flex-1 py-2 bg-red-600 text-white rounded-lg text-xs font-semibold flex items-center justify-center gap-1" data-testid={`reject-kyc-${k.id}`}>
                  <X size={14} /> Reject
                </button>
              </div>
            </div>
            );
          })}
        </div>
      )}

      {/* Disputes */}
      {!loading && subTab === "disputes" && (
        <div className="space-y-3">
          {disputes.length === 0 && <div className="text-center py-8 text-muted-foreground text-sm">No open disputes</div>}
          {disputes.map((d) => (
            <div key={d.id} className="bg-card border border-border rounded-xl p-3 space-y-2" data-testid={`dispute-row-${d.id}`}>
              <div className="flex items-center justify-between">
                <div className="text-sm font-semibold">Trade #{d.trade_id?.slice(0, 8)}</div>
                <span className="text-[10px] uppercase text-muted-foreground">{d.trade?.country}</span>
              </div>
              <div className="bg-background/50 rounded-lg p-2 text-xs space-y-1">
                <div>
                  <span className="text-muted-foreground">Raised by: </span>
                  <span className="text-primary font-semibold">{d.raised_by_name || d.raised_by?.slice(0, 8)}</span>
                  {d.raised_by_phone && <span className="text-muted-foreground"> · {d.raised_by_phone}</span>}
                </div>
                <div>
                  <span className="text-muted-foreground">Against: </span>
                  <span className="text-orange-300 font-semibold">{d.raised_against_name || d.raised_against?.slice(0, 8)}</span>
                </div>
                <div className="text-[10px] text-muted-foreground">{new Date(d.created_at).toLocaleString()}</div>
              </div>
              <div className="text-xs bg-background p-2 rounded">Reason: {d.reason}</div>
              {d.evidence_b64 && (
                <button
                  type="button"
                  onClick={() => setLightbox({ url: d.evidence_b64, title: `Dispute evidence #${d.id.slice(0,8)}` })}
                  className="block w-full rounded-lg overflow-hidden border border-border hover:border-primary transition group relative"
                  data-testid={`dispute-evidence-${d.id}`}
                >
                  <img src={d.evidence_b64} alt="evidence" className="w-full max-h-48 object-contain bg-black/40" />
                  <span className="absolute top-1 right-1 bg-black/70 text-white text-[10px] px-2 py-0.5 rounded-full flex items-center gap-1">
                    <ZoomIn size={10} /> Click to enlarge
                  </span>
                </button>
              )}
              {d.trade && (
                <div className="text-[11px] text-muted-foreground space-y-0.5 bg-background/40 rounded p-2">
                  <div>
                    <span className="text-blue-300">Buyer:</span>{" "}
                    <span className="text-white font-medium">{d.trade.buyer_name || d.trade.buyer_id?.slice(0,8)}</span>
                    {d.trade.buyer_phone && <span> · {d.trade.buyer_phone}</span>}
                  </div>
                  <div>
                    <span className="text-green-300">Seller:</span>{" "}
                    <span className="text-white font-medium">{d.trade.seller_name || d.trade.seller_id?.slice(0,8)}</span>
                    {d.trade.seller_phone && <span> · {d.trade.seller_phone}</span>}
                  </div>
                  <div>Amount: {d.trade.amount_usdt} USDT = {d.trade.amount_fiat} {d.trade.fiat}</div>
                  <div>Status: {d.trade.status}</div>
                  <div>UTR: {d.trade.utr_number || "—"}</div>
                  {d.trade.payment_screenshot_b64 && (
                    <button
                      type="button"
                      onClick={() => setLightbox({ url: d.trade.payment_screenshot_b64, title: `Payment screenshot · UTR ${d.trade.utr_number || "—"}` })}
                      className="text-primary underline cursor-pointer"
                      data-testid={`dispute-screenshot-${d.id}`}
                    >
                      View payment screenshot
                    </button>
                  )}
                </div>
              )}
              <div className="flex gap-2 pt-1">
                <button onClick={() => resolveDispute(d.id, "buyer_wins")} className="flex-1 py-2 bg-blue-600 text-white rounded-lg text-xs font-semibold" data-testid={`buyer-wins-${d.id}`}>
                  Buyer Wins (Release USDT)
                </button>
                <button onClick={() => resolveDispute(d.id, "seller_wins")} className="flex-1 py-2 bg-orange-600 text-white rounded-lg text-xs font-semibold" data-testid={`seller-wins-${d.id}`}>
                  Seller Wins (Refund)
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* All Trades */}
      {!loading && subTab === "trades" && (
        <div className="space-y-2">
          {trades.length === 0 && <div className="text-center py-8 text-muted-foreground text-sm">No trades yet</div>}
          {trades.map((t) => (
            <div key={t.id} className="bg-card border border-border rounded-xl p-3 text-xs space-y-1" data-testid={`trade-audit-${t.id}`}>
              <div className="flex items-center justify-between">
                <span className="font-mono">#{t.id.slice(0, 8)}</span>
                <span className={`px-2 py-0.5 rounded-full text-[10px] ${t.status === "completed" ? "bg-green-500/20 text-green-400" : t.status === "cancelled" ? "bg-red-500/20 text-red-400" : "bg-blue-500/20 text-blue-400"}`}>{t.status}</span>
              </div>
              <div>{t.amount_usdt} USDT @ {t.price_per_usdt} {t.fiat} • {t.country}</div>
              <div>
                <span className="text-blue-300">Buyer:</span>{" "}
                <span className="text-white font-medium">{t.buyer_name || t.buyer_id?.slice(0, 8)}</span>
                {t.buyer_phone && <span className="text-muted-foreground"> · {t.buyer_phone}</span>}
              </div>
              <div>
                <span className="text-green-300">Seller:</span>{" "}
                <span className="text-white font-medium">{t.seller_name || t.seller_id?.slice(0, 8)}</span>
                {t.seller_phone && <span className="text-muted-foreground"> · {t.seller_phone}</span>}
              </div>
              <div className="text-muted-foreground">{new Date(t.created_at).toLocaleString()}</div>
            </div>
          ))}
        </div>
      )}

      {/* Live Ads (admin view — shows REAL seller names, no codenames) */}
      {!loading && subTab === "ads" && (
        <div className="space-y-3" data-testid="admin-ads-tab">
          <div className="flex gap-2 flex-wrap">
            <select
              value={adsFilterCountry}
              onChange={(e) => setAdsFilterCountry(e.target.value)}
              className="bg-card border border-border rounded-lg px-2 py-1.5 text-xs"
              data-testid="admin-ads-country-filter"
            >
              <option value="">All countries</option>
              <option value="india">🇮🇳 India</option>
              <option value="pakistan">🇵🇰 Pakistan</option>
              <option value="nigeria">🇳🇬 Nigeria</option>
            </select>
            <select
              value={adsFilterStatus}
              onChange={(e) => setAdsFilterStatus(e.target.value)}
              className="bg-card border border-border rounded-lg px-2 py-1.5 text-xs"
              data-testid="admin-ads-status-filter"
            >
              <option value="active">Active</option>
              <option value="paused">Paused</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
              <option value="">All</option>
            </select>
            <button onClick={fetchAdminAds} className="px-3 py-1.5 bg-primary text-black rounded-lg text-xs font-semibold">Refresh</button>
            <span className="text-xs text-muted-foreground self-center">{adminAds.length} ads</span>
          </div>

          {adminAds.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground text-sm">No ads matching filters</div>
          ) : (
            adminAds.map((ad) => (
              <div key={ad.id} className="bg-card border border-border rounded-xl p-3 space-y-1.5" data-testid={`admin-ad-row-${ad.id}`}>
                <div className="flex items-center justify-between">
                  <div className="text-sm font-semibold text-white">{ad.seller_real_name}</div>
                  <span className="text-[10px] uppercase px-2 py-0.5 rounded-full bg-primary/15 text-primary">
                    {ad.seller_country === "nigeria" ? "🇳🇬 NG" : ad.seller_country === "pakistan" ? "🇵🇰 PK" : "🇮🇳 IN"}
                  </span>
                </div>
                <div className="text-[11px] text-muted-foreground">
                  Phone: {ad.seller_phone || "—"} · ID: <span className="font-mono">{ad.seller_id?.slice(0, 8)}</span>
                </div>
                <div className="grid grid-cols-3 gap-1 text-xs">
                  <div><span className="text-muted-foreground">Price:</span> <b className="text-primary">{ad.price_per_usdt} {ad.fiat}</b></div>
                  <div><span className="text-muted-foreground">Avail:</span> <b>{Number(ad.available_amount_usdt || 0).toFixed(2)} USDT</b></div>
                  <div><span className="text-muted-foreground">Limits:</span> <b>{ad.min_limit_fiat}–{ad.max_limit_fiat}</b></div>
                </div>
                <div className="text-[10px] text-muted-foreground">
                  Payment: {(ad.payment_methods || []).map((p) => p.method?.replace("_", " ")).join(", ") || "Bank Transfer"}
                  {ad.bank_details && (
                    <span className="ml-1">· Bank: {ad.bank_details.bank_name} A/C {ad.bank_details.account_number}</span>
                  )}
                </div>
                <div className="flex items-center justify-between text-[10px] text-muted-foreground">
                  <span>Status: <span className={`uppercase ${ad.status === "active" ? "text-green-400" : "text-red-400"}`}>{ad.status}</span> · {ad.completed_trades || 0} trades done</span>
                  <span>{new Date(ad.created_at).toLocaleString()}</span>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* === Image Lightbox (KYC docs / dispute evidence / payment screenshots) ===
          Chrome blocks navigation to data: URLs in new tabs, so we render inline. */}
      {lightbox && (
        <Lightbox lightbox={lightbox} onClose={() => setLightbox(null)} />
      )}
    </div>
  );
};

export default P2PAdminPanel;

const StatCard = ({ label, value, hint, testId }) => (
  <div className="bg-card border border-border rounded-xl p-3 space-y-1" data-testid={testId}>
    <div className="text-xs text-muted-foreground">{label}</div>
    <div className="text-2xl font-bold text-primary">{value}</div>
    {hint && <div className="text-[10px] text-muted-foreground">{hint}</div>}
  </div>
);

const Thumb = ({ url, label, onOpen, testId }) => {
  if (!url) {
    return (
      <div className="rounded-lg w-full h-24 bg-background border border-dashed border-border flex items-center justify-center text-[10px] text-muted-foreground text-center px-2">
        {label}<br />(purged)
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={() => onOpen({ url, title: label })}
      className="relative rounded-lg w-full h-24 overflow-hidden border border-border hover:border-primary transition group"
      data-testid={testId}
      title={`Click to enlarge: ${label}`}
    >
      <img src={url} alt={label} className="w-full h-full object-cover" />
      <span className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition flex items-center justify-center">
        <ZoomIn size={20} className="text-white opacity-0 group-hover:opacity-100 transition" />
      </span>
      <span className="absolute bottom-0 left-0 right-0 bg-black/70 text-white text-[9px] px-1 py-0.5 truncate">
        {label}
      </span>
    </button>
  );
};

const Lightbox = ({ lightbox, onClose }) => {
  const [zoom, setZoom] = useState(1);

  // ESC to close
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const handleDownload = () => {
    try {
      const a = document.createElement("a");
      a.href = lightbox.url;
      const ext = (lightbox.url.match(/^data:image\/(\w+)/) || [])[1] || "jpg";
      a.download = `${(lightbox.title || "image").replace(/[^a-z0-9-_]+/gi, "_")}.${ext}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (e) {
      toast.error("Download failed");
    }
  };

  return (
    <div
      className="fixed inset-0 z-[80] bg-black/95 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="admin-image-lightbox"
    >
      <div
        className="relative w-full h-full flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Top bar */}
        <div className="flex items-center justify-between text-white pb-3">
          <div className="text-sm font-semibold truncate pr-3">{lightbox.title}</div>
          <div className="flex items-center gap-2">
            <button onClick={() => setZoom((z) => Math.max(0.5, z - 0.25))} className="px-2 py-1 bg-white/10 hover:bg-white/20 rounded text-xs" data-testid="lightbox-zoom-out">−</button>
            <span className="text-xs w-12 text-center">{Math.round(zoom * 100)}%</span>
            <button onClick={() => setZoom((z) => Math.min(4, z + 0.25))} className="px-2 py-1 bg-white/10 hover:bg-white/20 rounded text-xs" data-testid="lightbox-zoom-in">+</button>
            <button onClick={handleDownload} className="px-2 py-1 bg-white/10 hover:bg-white/20 rounded text-xs flex items-center gap-1" data-testid="lightbox-download">
              <Download size={12} /> Save
            </button>
            <button onClick={onClose} className="p-1.5 bg-white/10 hover:bg-red-500/40 rounded" data-testid="lightbox-close">
              <X size={16} className="text-white" />
            </button>
          </div>
        </div>
        {/* Image */}
        <div className="flex-1 overflow-auto flex items-center justify-center">
          <img
            src={lightbox.url}
            alt={lightbox.title}
            style={{ transform: `scale(${zoom})`, transformOrigin: "center center", transition: "transform 0.15s" }}
            className="max-w-full max-h-full object-contain select-none"
          />
        </div>
        <div className="text-center text-[10px] text-white/50 pt-2">Click outside or press ESC to close</div>
      </div>
    </div>
  );
};
